/**
 * Source of truth for indexable marketing routes.
 *
 * Consumed by the XML sitemap and the marketing footer. When you add a new
 * public marketing page under app/routes/_home+/, add it here with a real
 * lastModified (YYYY-MM-DD) reflecting the last substantive edit to the
 * content. Google uses lastmod heavily; making it accurate matters.
 *
 * Keep entries ordered by importance (the home page first).
 */
export type MarketingRoute = {
  path: string
  /** Used as anchor text in the footer "Resources/Product" lists. */
  label: string
  /** Optional grouping for the footer. Omit to hide from the footer. */
  footerSection?: 'Product' | 'Resources'
  /** YYYY-MM-DD. Bump only when the content actually changes. */
  lastModified: string
  /** Sitemap-only hints; both default sensibly. */
  changefreq?: 'always' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'never'
  priority?: string
}

export const MARKETING_ROUTES: MarketingRoute[] = [
  {
    path: '/',
    label: 'Home',
    lastModified: '2026-05-18',
    changefreq: 'weekly',
    priority: '1.0',
  },
  {
    path: '/email-to-webhook',
    label: 'Email to Webhook',
    footerSection: 'Product',
    lastModified: '2026-07-12',
    changefreq: 'monthly',
    priority: '0.9',
  },
  {
    path: '/inbound-email-api',
    label: 'Inbound Email API',
    footerSection: 'Product',
    lastModified: '2026-07-20',
    changefreq: 'monthly',
    priority: '0.8',
  },
  {
    path: '/cloudmailin-alternative',
    label: 'CloudMailin Alternative',
    footerSection: 'Product',
    lastModified: '2026-05-18',
    changefreq: 'monthly',
    priority: '0.8',
  },
  {
    path: '/guides/inbound-email-webhook',
    label: 'Inbound Email Webhook Guide',
    footerSection: 'Resources',
    lastModified: '2026-07-20',
    changefreq: 'monthly',
    priority: '0.8',
  },
  {
    path: '/guides/receive-inbound-email-as-json-nextjs',
    label: 'Receive Inbound Email as JSON in Next.js',
    footerSection: 'Resources',
    lastModified: '2026-07-20',
    changefreq: 'monthly',
    priority: '0.8',
  },
  {
    path: '/guides/forward-email-to-slack-without-zapier',
    label: 'Forward Email to Slack (Without Zapier)',
    footerSection: 'Resources',
    lastModified: '2026-07-07',
    changefreq: 'monthly',
    priority: '0.8',
  },
  {
    path: '/guides/webhook-to-send-email',
    label: 'Webhook to Send Email',
    footerSection: 'Resources',
    lastModified: '2026-07-12',
    changefreq: 'monthly',
    priority: '0.8',
  },
]

export function routesInSection(
  section: NonNullable<MarketingRoute['footerSection']>,
): MarketingRoute[] {
  return MARKETING_ROUTES.filter((r) => r.footerSection === section)
}
